import { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import useAuth from "../hooks/useAuth";
import api from "../api/client";
import PdsAdsImportModal from "../components/modals/PdsAdsImportModal";
import PdsAdsDetailModal from "../components/modals/PdsAdsDetailModal";

const PAGE_SIZE = 50;

export default function PdsAdsPage() {
  const isMobile = useIsMobile();
  const { hasPerm } = useAuth();
  const canImport = hasPerm("pds_ads.import");

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const debounceRef = useRef(null);

  // Debounce the search box.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setDebounced(search.trim());
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pds-ads", {
        params: { search: debounced || undefined, page, page_size: PAGE_SIZE },
      });
      setColumns(data.columns);
      setRows(data.rows);
      setTotal(data.total);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [debounced, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <a href="/reports" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </a>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">PDS/ADS</h1>
            <p className="text-sm text-gray-500">
              Permanently / Automatically Disconnected Subscribers — search for potential returning subscribers.
            </p>
          </div>
          {canImport && (
            <button
              onClick={() => setShowImport(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import Excel
            </button>
          )}
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, account no, mobile, address, NAP code…"
              className="border rounded px-3 py-1.5 text-sm w-full"
            />
          </div>
          <div className="text-sm text-gray-500 pb-1.5">
            <strong className="text-gray-800">{total.toLocaleString()}</strong> record(s)
          </div>
        </div>

        {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-lg shadow px-4 py-12 text-center text-gray-400">
            {debounced ? "No records match your search." : "No PDS/ADS records yet. Use Import Excel to add data."}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {columns.map((c) => (
                    <th key={c.field} className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r.id}
                    onClick={() => setDetailId(r.id)}
                    className={`cursor-pointer hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                  >
                    {columns.map((c) => (
                      <td key={c.field} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[280px] truncate">
                        {r[c.field] || <span className="text-gray-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 border rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 border rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showImport && (
        <PdsAdsImportModal
          onClose={() => setShowImport(false)}
          onImported={fetchData}
        />
      )}

      {detailId && <PdsAdsDetailModal recordId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
